/**
 * Food Ordering API Service
 *
 * This service extends the existing API client with food ordering specific
 * functionality and data transformations for the customer web app.
 */

import { apiClient } from '@neighbourhood-munchies/api-client'
import {
    MenuCustomisation,
    MenuItem,
    ObjectId,
    Order,
    OrderStatus,
    Restaurant,
    Zone,
    ZoneMapping,
} from '@neighbourhood-munchies/shared-types'
import {
    CartItem,
    CreateOrderRequest,
    DeliveryAddress,
    MenuItemDisplayData,
    MenuResponse,
    OrderResponse,
    OrderStatusUpdate,
    OrderTrackingData,
    PaymentMethod,
    RestaurantDisplayData,
    RestaurantFilters,
    RestaurantListResponse,
} from '../types'
import { isRestaurantOpen } from '../utils/restaurant'

// ============================================================================
// RESTAURANT SERVICES
// ============================================================================

export const restaurantService = {
  /**
   * Get active restaurants ordered by package score for customer display
   * Supports filtering by search, cuisine type, delivery time, rating, and open status
   * Supports sorting by package score, name, rating, and delivery time
   */
  async getActiveRestaurants(filters?: RestaurantFilters): Promise<RestaurantListResponse> {
    const params = {
      is_active: true,
      ...(filters?.search && { search: filters.search }),
      ...(filters?.cuisineType && { type: filters.cuisineType }),
    }

    const response = await apiClient.restaurants.getAll(params)

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch restaurants')
    }

    // Transform restaurants to display data, excluding unassigned (package_score === 0)
    const restaurants: RestaurantDisplayData[] = response.data
      .filter(r => r.package_score !== 0)
      .map(transformRestaurantToDisplayData)

    // Apply client-side filtering and sorting
    let filteredRestaurants = restaurants

    // Filter by open status
    if (filters?.isOpen !== undefined) {
      filteredRestaurants = filteredRestaurants.filter(r => r.isOpen === filters.isOpen)
    }

    // Filter by maximum delivery time
    if (filters?.maxDeliveryTime) {
      filteredRestaurants = filteredRestaurants.filter(r => {
        const deliveryTime = parseInt(r.estimatedDeliveryTime.split('-')[0]) // Get the lower bound
        return deliveryTime <= filters.maxDeliveryTime!
      })
    }

    // Filter by minimum rating
    if (filters?.minRating) {
      filteredRestaurants = filteredRestaurants.filter(r =>
        (r.rating || 0) >= filters.minRating!
      )
    }

    // Sort restaurants
    const sortBy = filters?.sortBy || 'package_score'
    const sortOrder = filters?.sortOrder || 'asc'

    filteredRestaurants.sort((a, b) => {
      let aValue: any, bValue: any

      switch (sortBy) {
        case 'package_score':
          aValue = a.package_score
          bValue = b.package_score
          break
        case 'name':
          aValue = a.name.toLowerCase()
          bValue = b.name.toLowerCase()
          break
        case 'delivery_time':
          // Parse delivery time range and use the lower bound for sorting
          aValue = parseInt(a.estimatedDeliveryTime.split('-')[0])
          bValue = parseInt(b.estimatedDeliveryTime.split('-')[0])
          break
        default:
          aValue = a.package_score
          bValue = b.package_score
      }

      if (sortOrder === 'desc') {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0
      } else {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0
      }
    })

    return {
      restaurants: filteredRestaurants,
      total: filteredRestaurants.length,
      page: 1,
      limit: filteredRestaurants.length,
    }
  },

  /**
   * Get restaurant details by ID for customer display
   */
  async getRestaurantDetails(restaurantId: ObjectId): Promise<RestaurantDisplayData> {
    const response = await apiClient.restaurants.getById(restaurantId)

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Restaurant not found')
    }

    return transformRestaurantToDisplayData(response.data)
  },

  /**
   * Get restaurant details with menu for customer display
   * NOTE: Customizations are NOT fetched here - they are loaded on-demand when menu item modal opens
   */
  async getRestaurantWithMenu(restaurantId: ObjectId): Promise<MenuResponse> {
    // Fetch restaurant details and menu items in parallel
    const [restaurantResponse, menuItemsResponse] = await Promise.all([
      apiClient.restaurants.getById(restaurantId),
      apiClient.menuItems.getByRestaurant(restaurantId)
    ])

    if (!restaurantResponse.success || !restaurantResponse.data) {
      throw new Error(restaurantResponse.error || 'Restaurant not found')
    }

    if (!menuItemsResponse.success || !menuItemsResponse.data) {
      throw new Error(menuItemsResponse.error || 'Failed to fetch menu items')
    }

    const restaurant = transformRestaurantToDisplayData(restaurantResponse.data)

    // Transform menu items WITHOUT fetching customizations (lazy load on modal open)
    const menuItems = menuItemsResponse.data.map(item =>
      transformMenuItemToDisplayData(item, [])
    )

    // Get unique categories
    const categories = Array.from(new Set(
      menuItems.map(item => item.category).filter(Boolean)
    )) as string[]

    return {
      restaurant,
      menuItems,
      categories,
    }
  },

  /**
   * Get menu items for a specific restaurant with filtering
   * NOTE: Customizations are NOT fetched here - they are loaded on-demand when menu item modal opens
   */
  async getMenuItems(restaurantId: ObjectId, filters?: {
    category?: string;
    search?: string;
  }): Promise<MenuItemDisplayData[]> {
    const params = {
      ...(filters?.category && { category: filters.category }),
      ...(filters?.search && { search: filters.search }),
    }

    const response = await apiClient.menuItems.getByRestaurant(restaurantId, params)

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch menu items')
    }

    // Transform menu items WITHOUT fetching customizations (lazy load on modal open)
    const menuItems = response.data.map(item =>
      transformMenuItemToDisplayData(item, [])
    )

    return menuItems
  },

  /**
   * Get menu categories for a restaurant
   */
  async getMenuCategories(restaurantId: ObjectId): Promise<string[]> {
    const response = await apiClient.menuItems.getCategories(restaurantId)

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch menu categories')
    }

    return response.data
  },

  /**
   * Get menu item details with customizations
   */
  async getMenuItemDetails(menuItemId: ObjectId): Promise<MenuItemDisplayData> {
    const [itemResponse, customizationsResponse] = await Promise.all([
      apiClient.menuItems.getById(menuItemId),
      apiClient.menuItems.getMenuCustomisations(menuItemId)
    ])

    if (!itemResponse.success || !itemResponse.data) {
      throw new Error(itemResponse.error || 'Menu item not found')
    }

    const customizations = customizationsResponse.success ? customizationsResponse.data || [] : []

    return transformMenuItemToDisplayData(itemResponse.data, customizations)
  },

  /**
   * Get restaurants by delivery zone
   */
  async getRestaurantsByZone(zoneId: ObjectId, filters?: Omit<RestaurantFilters, 'search'>): Promise<RestaurantListResponse> {
    const params = {
      is_active: true,
      zone_id: zoneId,
    }

    const response = await apiClient.restaurants.getAll(params)

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch restaurants for zone')
    }

    // Transform and filter restaurants, excluding unassigned (package_score === 0)
    const restaurants: RestaurantDisplayData[] = response.data
      .filter(r => r.package_score !== 0)
      .map(transformRestaurantToDisplayData)

    // Apply the same filtering logic as getActiveRestaurants
    let filteredRestaurants = restaurants

    if (filters?.isOpen !== undefined) {
      filteredRestaurants = filteredRestaurants.filter(r => r.isOpen === filters.isOpen)
    }

    if (filters?.maxDeliveryTime) {
      filteredRestaurants = filteredRestaurants.filter(r => {
        const deliveryTime = parseInt(r.estimatedDeliveryTime.split('-')[0])
        return deliveryTime <= filters.maxDeliveryTime!
      })
    }

    if (filters?.minRating) {
      filteredRestaurants = filteredRestaurants.filter(r =>
        (r.rating || 0) >= filters.minRating!
      )
    }

    // Sort restaurants
    const sortBy = filters?.sortBy || 'package_score'
    const sortOrder = filters?.sortOrder || 'asc'

    filteredRestaurants.sort((a, b) => {
      let aValue: any, bValue: any

      switch (sortBy) {
        case 'package_score':
          aValue = a.package_score
          bValue = b.package_score
          break
        case 'name':
          aValue = a.name.toLowerCase()
          bValue = b.name.toLowerCase()
          break
        case 'rating':
          aValue = a.rating || 0
          bValue = b.rating || 0
          break
        case 'delivery_time':
          aValue = parseInt(a.estimatedDeliveryTime.split('-')[0])
          bValue = parseInt(b.estimatedDeliveryTime.split('-')[0])
          break
        default:
          aValue = a.package_score
          bValue = b.package_score
      }

      if (sortOrder === 'desc') {
        return aValue < bValue ? 1 : aValue > bValue ? -1 : 0
      } else {
        return aValue > bValue ? 1 : aValue < bValue ? -1 : 0
      }
    })

    return {
      restaurants: filteredRestaurants,
      total: filteredRestaurants.length,
      page: 1,
      limit: filteredRestaurants.length,
    }
  },

  /**
   * Search restaurants by name or cuisine with advanced filtering
   */
  async searchRestaurants(query: string, filters?: RestaurantFilters): Promise<RestaurantListResponse> {
    const searchFilters: RestaurantFilters = {
      ...filters,
      search: query,
    }

    return this.getActiveRestaurants(searchFilters)
  },
}

// ============================================================================
// ORDER SERVICES
// ============================================================================

export const orderService = {
  /**
   * Create a new order
   */
  async createOrder(orderData: CreateOrderRequest): Promise<OrderResponse> {
    // Validate order data before submission
    if (!orderData.restaurantId || !orderData.items.length) {
      throw new Error('Invalid order data: Restaurant and items are required')
    }

    // Transform the order data to match the backend API format
    const backendOrderData = {
      restaurant_id: orderData.restaurantId,
      delivery_address: [orderData.deliveryAddress.street, orderData.deliveryAddress.city, orderData.deliveryAddress.postalCode]
        .filter(Boolean)
        .join(', '),
      delivery_zone_id: orderData.deliveryAddress.zone,
      order_items: orderData.items.map(item => ({
        menu_item_id: item.menuItemId,
        name: 'Menu Item', // Will be populated by backend
        price: 0, // Will be populated by backend
        quantity: item.quantity,
        customisations: item.customizations.map(customization => ({
          customisation_id: customization.customizationId,
          customisation_name: 'Customization', // Will be populated by backend
          selected_items: customization.selectedOptions.map(optionId => ({
            item_id: optionId,
            name: '', // Will be populated by backend
            price: 0,  // Will be populated by backend
          }))
        })),
        special_instructions: item.specialInstructions,
      })),
      payment_method: orderData.paymentMethod.type,
      notes: orderData.deliveryNotes,
      delivery_fee: orderData.deliveryFee,
      // Customer info will be handled by authentication
    }

    const response = await apiClient.orders.create(backendOrderData)

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to create order')
    }

    return {
      order: response.data,
      estimatedDeliveryTime: '30-45 minutes', // This should come from backend calculation
      trackingUrl: `/orders/${response.data.id}/track`,
    }
  },

  /**
   * Get order by ID for tracking
   */
  async getOrder(orderId: ObjectId): Promise<Order> {
    const response = await apiClient.orders.getById(orderId)

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Order not found')
    }

    return response.data
  },

  /**
   * Get order history for the current user
   */
  async getOrderHistory(params?: {
    page?: number;
    limit?: number;
    status?: string;
    customer_id?: string;
  }): Promise<{
    orders: Order[];
    total: number;
    page: number;
    limit: number;
  }> {
    const queryParams = {
      page: params?.page || 1,
      limit: params?.limit || 20,
      ...(params?.status && { status: params.status }),
      ...(params?.customer_id && { customer_id: params.customer_id }),
    }

    const response = await apiClient.orders.getAll(queryParams)

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch order history')
    }

    return {
      orders: response.data,
      total: response.pagination?.total || 0,
      page: response.pagination?.page || 1,
      limit: response.pagination?.limit || 20,
    }
  },

  /**
   * Get order tracking information with status updates
   */
  async getOrderTracking(orderId: ObjectId): Promise<OrderTrackingData> {
    const order = await this.getOrder(orderId)

    // Generate status history based on current status
    const statusHistory = generateOrderStatusHistory(order)

    // Calculate estimated delivery time based on order creation and status
    const estimatedDeliveryTime = calculateEstimatedDeliveryTime(order)

    return {
      ...order,
      estimatedDeliveryTime,
      statusHistory,
      // currentLocation would be populated by delivery tracking system
      currentLocation: undefined,
    }
  },

  /**
   * Cancel an order (if allowed)
   */
  async cancelOrder(orderId: ObjectId, _reason?: string): Promise<Order> {
    // Check if order can be cancelled (only pending/confirmed orders)
    const order = await this.getOrder(orderId)

    if (!['Pending', 'Confirmed'].includes(order.order_status)) {
      throw new Error('Order cannot be cancelled at this stage')
    }

    const response = await apiClient.orders.updateStatus(orderId, 'Cancelled')

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to cancel order')
    }

    return response.data
  },

  /**
   * Reorder - create a new order based on a previous order
   */
  async reorder(orderId: ObjectId, deliveryAddress?: DeliveryAddress): Promise<OrderResponse> {
    const originalOrder = await this.getOrder(orderId)

    if (!originalOrder.order_items || !originalOrder.restaurant_id) {
      throw new Error('Cannot reorder: Original order data incomplete')
    }

    // Transform original order items back to CreateOrderRequest format
    const orderData: CreateOrderRequest = {
      restaurantId: originalOrder.restaurant_id,
      items: originalOrder.order_items.map(item => ({
        menuItemId: item.menu_item_id || '',
        quantity: item.quantity || 1,
        customizations: item.customisations?.map(customization => ({
          customizationId: customization.customisation_id,
          selectedOptions: customization.selected_items.map(selectedItem => selectedItem.item_id),
        })) || [],
        specialInstructions: item.special_instructions,
      })),
      deliveryAddress: deliveryAddress || {
        street: originalOrder.delivery_address || '',
        city: '',
        postalCode: '',
        zone: originalOrder.delivery_zone_id,
      },
      paymentMethod: {
        type: (originalOrder.payment_method as any) || 'card',
      },
      deliveryNotes: originalOrder.notes,
      customerInfo: {
        name: '', // Will be populated from auth
        phone: '', // Will be populated from auth
      },
    }

    return this.createOrder(orderData)
  },
}

// ============================================================================
// PAYMENT PROCESSING SERVICES
// ============================================================================

export const paymentService = {
  /**
   * Validate payment method before order submission
   */
  async validatePaymentMethod(paymentMethod: PaymentMethod): Promise<boolean> {
    // For now, we'll accept all payment methods
    // In a real implementation, this would validate card details, etc.
    switch (paymentMethod.type) {
      case 'cash':
        // Cash on delivery is always valid
        return true
      case 'linx':
        // Linx payment is always valid
        return true
      default:
        return false
    }
  },

  /**
   * Process payment for an order
   * This is a placeholder for payment gateway integration
   */
  async processPayment(orderId: ObjectId, paymentMethod: PaymentMethod, amount: number): Promise<{
    success: boolean;
    transactionId?: string;
    error?: string;
  }> {
    // This would integrate with actual payment processors like Stripe, PayPal, etc.
    // For now, we'll simulate payment processing

    try {
      // Simulate payment processing delay
      await new Promise(resolve => setTimeout(resolve, 1000))

      if (paymentMethod.type === 'cash') {
        return {
          success: true,
          transactionId: `cash_${orderId}_${Date.now()}`,
        }
      }

      // Simulate card/digital wallet processing with amount validation
      if (amount <= 0) {
        throw new Error('Invalid payment amount')
      }

      const transactionId = `txn_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`

      return {
        success: true,
        transactionId,
      }
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Payment processing failed',
      }
    }
  },

  /**
   * Get available payment methods for the user
   */
  async getAvailablePaymentMethods(): Promise<PaymentMethod[]> {
    // This would fetch user's saved payment methods from backend
    // For now, return default options
    return [
      { type: 'cash' },
      { type: 'linx' },
    ]
  },

  /**
   * Calculate order total including fees (no tax)
   */
  calculateOrderTotal(items: CartItem[], deliveryFee: number = 0): {
    subtotal: number;
    deliveryFee: number;
    total: number;
  } {
    const subtotal = items.reduce((sum, item) => sum + item.totalPrice, 0)
    const total = subtotal + deliveryFee

    return {
      subtotal: Math.round(subtotal * 100) / 100,
      deliveryFee: Math.round(deliveryFee * 100) / 100,
      total: Math.round(total * 100) / 100,
    }
  },
}

// ============================================================================
// DELIVERY ZONE SERVICES
// ============================================================================

export const deliveryService = {
  /**
   * Get delivery zones
   */
  async getDeliveryZones(): Promise<Zone[]> {
    const response = await apiClient.zones.getAll()

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to fetch delivery zones')
    }

    return response.data
  },

  /**
   * Get delivery cost for a specific zone
   */
  async getDeliveryCost(zoneId: ObjectId): Promise<number> {
    const response = await apiClient.zones.getDeliveryCost(zoneId)

    if (!response.success || !response.data) {
      throw new Error(response.error || 'Failed to get delivery cost')
    }

    return response.data.delivery_cost
  },

  /**
   * Calculate delivery cost between two zones using zone mappings
   * Falls back to destination zone's base cost if no mapping exists
   */
  async calculateDeliveryCostBetweenZones(fromZoneId: ObjectId, toZoneId: ObjectId): Promise<{
    deliveryCost: number;
    source: 'zone_mapping' | 'base_zone_cost';
  }> {
    try {
      const response = await apiClient.zoneMappings.calculateDeliveryCost(fromZoneId, toZoneId)

      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to calculate delivery cost')
      }

      return {
        deliveryCost: response.data.delivery_cost,
        source: response.data.source
      }
    } catch (error) {
      console.warn(`Failed to calculate delivery cost between zones ${fromZoneId} -> ${toZoneId}:`, error)

      // Fallback to destination zone's base cost
      try {
        const baseCost = await this.getDeliveryCost(toZoneId)
        return {
          deliveryCost: baseCost,
          source: 'base_zone_cost'
        }
      } catch (fallbackError) {
        console.error('Failed to get fallback delivery cost:', fallbackError)
        throw new Error('Unable to calculate delivery cost')
      }
    }
  },

  /**
   * Validate delivery address and determine zone
   */
  async validateDeliveryAddress(address: DeliveryAddress, restaurantZoneId?: ObjectId): Promise<{
    isValid: boolean;
    zone?: Zone;
    estimatedDeliveryTime?: string;
    deliveryCost?: number;
    error?: string;
  }> {
    try {
      const zones = await this.getDeliveryZones()

      // Simple zone matching based on postal code or city
      // In a real implementation, this would use geocoding and polygon matching
      const matchingZone = zones.find(zone =>
        zone.name.toLowerCase().includes((address.city || '').toLowerCase()) ||
        zone.name.toLowerCase().includes((address.postalCode || '').toLowerCase())
      )

      if (!matchingZone) {
        return {
          isValid: false,
          error: 'Delivery not available in this area',
        }
      }

      // Calculate delivery cost using zone mappings if restaurant zone is provided
      let deliveryCost: number | undefined
      if (restaurantZoneId) {
        try {
          const costResult = await this.calculateDeliveryCostBetweenZones(restaurantZoneId, matchingZone.id)
          deliveryCost = costResult.deliveryCost
        } catch (error) {
          console.warn('Failed to calculate zone mapping cost, using base zone cost:', error)
          deliveryCost = matchingZone.delivery_cost
        }
      } else {
        // Use base zone cost if no restaurant zone provided
        deliveryCost = matchingZone.delivery_cost
      }

      return {
        isValid: true,
        zone: matchingZone,
        estimatedDeliveryTime: '30-45 minutes', // This would be calculated based on distance and traffic
        deliveryCost,
      }
    } catch (error) {
      return {
        isValid: false,
        error: error instanceof Error ? error.message : 'Address validation failed',
      }
    }
  },
}

// ============================================================================
// ZONE MAPPING SERVICES
// ============================================================================

export const zoneMappingService = {
  /**
   * Get all zone mappings with enhanced error handling
   */
  async getZoneMappings(): Promise<ZoneMapping[]> {
    const maxRetries = 2
    let lastError: Error | null = null

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        const response = await apiClient.zoneMappings.getAll()

        if (!response.success || !response.data) {
          throw new Error(response.error || 'Failed to fetch zone mappings')
        }

        return response.data
      } catch (error) {
        lastError = error instanceof Error ? error : new Error('Unknown error')

        if (attempt < maxRetries) {
          const delay = 1000 * attempt // Linear backoff for zone mappings
          await new Promise(resolve => setTimeout(resolve, delay))
          console.warn(`Zone mappings API attempt ${attempt} failed, retrying in ${delay}ms:`, error)
        }
      }
    }

    console.error('Failed to fetch zone mappings after retries:', lastError)
    throw lastError || new Error('Zone mappings service unavailable')
  },

  /**
   * Get delivery cost between two zones with enhanced retry mechanism and circuit breaker
   */
  async getDeliveryCost(fromZoneId: ObjectId, toZoneId: ObjectId): Promise<number> {
    const maxRetries = 3
    let lastError: Error | null = null

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        // Add timeout to prevent hanging requests
        const timeoutPromise = new Promise<never>((_, reject) => {
          setTimeout(() => reject(new Error('Request timeout')), 10000) // 10 second timeout
        })

        const apiPromise = apiClient.zoneMappings.calculateDeliveryCost(fromZoneId, toZoneId)
        const response = await Promise.race([apiPromise, timeoutPromise])

        if (!response.success || !response.data) {
          throw new Error(response.error || 'Failed to get delivery cost')
        }

        return response.data.delivery_cost
      } catch (error) {
        lastError = error instanceof Error ? error : new Error('Unknown error')

        // Determine if error is retryable
        const isRetryable = this.isRetryableError(error)

        if (attempt < maxRetries && isRetryable) {
          // Exponential backoff with jitter: base delay + random component
          const baseDelay = Math.pow(2, attempt - 1) * 1000
          const jitter = Math.random() * 500 // Add up to 500ms jitter
          const delay = baseDelay + jitter

          await new Promise(resolve => setTimeout(resolve, delay))
          console.warn(`Zone mapping API attempt ${attempt} failed (${lastError.message}), retrying in ${Math.round(delay)}ms`)
        } else if (!isRetryable) {
          console.warn(`Zone mapping API failed with non-retryable error: ${lastError.message}`)
          break
        }
      }
    }

    throw lastError || new Error('Failed to get delivery cost after retries')
  },

  /**
   * Determine if an error is retryable
   */
  isRetryableError(error: unknown): boolean {
    if (!(error instanceof Error)) return false

    const message = error.message.toLowerCase()

    // Network errors are retryable
    if (message.includes('network') ||
      message.includes('timeout') ||
      message.includes('connection') ||
      message.includes('fetch')) {
      return true
    }

    // Server errors (5xx) are retryable
    if (message.includes('server error') ||
      message.includes('internal server error') ||
      message.includes('service unavailable') ||
      message.includes('bad gateway')) {
      return true
    }

    // Client errors (4xx) are generally not retryable
    if (message.includes('not found') ||
      message.includes('unauthorized') ||
      message.includes('forbidden') ||
      message.includes('bad request')) {
      return false
    }

    // Default to retryable for unknown errors
    return true
  },

  /**
   * Get delivery cost with comprehensive fallback strategy
   */
  async getDeliveryCostWithFallback(fromZoneId: ObjectId, toZoneId: ObjectId): Promise<number> {
    try {
      // Try to get specific zone mapping first
      return await this.getDeliveryCost(fromZoneId, toZoneId)
    } catch (mappingError) {
      console.warn(`Zone mapping failed for ${fromZoneId} -> ${toZoneId}:`, mappingError)

      // Fallback strategy 1: Try destination zone's default delivery cost
      try {
        const zones = await deliveryService.getDeliveryZones()
        const destinationZone = zones.find(zone => zone.id === toZoneId)

        if (destinationZone) {
          return destinationZone.delivery_cost
        }

        console.warn(`Destination zone ${toZoneId} not found in cached zones, trying API`)

        // Fallback strategy 2: Try to get destination zone directly from API
        const zoneResponse = await apiClient.zones.getDeliveryCost(toZoneId)
        if (zoneResponse.success && zoneResponse.data) {
          return zoneResponse.data.delivery_cost
        }

        throw new Error(`Could not retrieve destination zone ${toZoneId} delivery cost`)
      } catch (fallbackError) {
        console.error('All fallback strategies failed:', fallbackError)

        // Fallback strategy 3: Use source zone default if available
        try {
          const zones = await deliveryService.getDeliveryZones()
          const sourceZone = zones.find(zone => zone.id === fromZoneId)

          if (sourceZone) {
            return sourceZone.delivery_cost
          }
        } catch (sourceZoneError) {
          console.warn('Could not get source zone default:', sourceZoneError)
        }

        // Fallback strategy 4: Return system default
        return 15.00
      }
    }
  },

  /**
   * Calculate delivery fee with comprehensive error handling and graceful degradation
   */
  async calculateDeliveryFee(restaurantZoneId: ObjectId, userZoneId: ObjectId): Promise<{
    deliveryFee: number;
    source: 'zone_mapping' | 'zone_default' | 'source_zone_default' | 'system_fallback';
    error?: string;
    warning?: string;
  }> {
    // Input validation
    if (!restaurantZoneId || !userZoneId) {
      return {
        deliveryFee: 15.00,
        source: 'system_fallback',
        error: 'Invalid zone information provided. Using default delivery fee.'
      }
    }

    try {
      // Primary strategy: Try zone mapping
      const mappedCost = await this.getDeliveryCost(restaurantZoneId, userZoneId)
      return {
        deliveryFee: mappedCost,
        source: 'zone_mapping'
      }
    } catch (mappingError) {
      console.warn('Zone mapping calculation failed:', mappingError)

      // Secondary strategy: Try destination zone default
      try {
        const zones = await deliveryService.getDeliveryZones()
        const userZone = zones.find(zone => zone.id === userZoneId)

        if (userZone) {
          return {
            deliveryFee: userZone.delivery_cost,
            source: 'zone_default',
            warning: 'Using standard delivery rate for your area.'
          }
        }

        // Try API for destination zone
        const zoneResponse = await apiClient.zones.getDeliveryCost(userZoneId)
        if (zoneResponse.success && zoneResponse.data) {
          return {
            deliveryFee: zoneResponse.data.delivery_cost,
            source: 'zone_default',
            warning: 'Using standard delivery rate for your area.'
          }
        }

        throw new Error('Destination zone not found')
      } catch (destinationZoneError) {
        console.warn('Destination zone fallback failed:', destinationZoneError)

        // Tertiary strategy: Try source zone default
        try {
          const zones = await deliveryService.getDeliveryZones()
          const restaurantZone = zones.find(zone => zone.id === restaurantZoneId)

          if (restaurantZone) {
            return {
              deliveryFee: restaurantZone.delivery_cost,
              source: 'source_zone_default',
              warning: 'Using restaurant area delivery rate. Final rate may vary.'
            }
          }

          throw new Error('Source zone not found')
        } catch (sourceZoneError) {
          console.error('All delivery fee calculation strategies failed:', sourceZoneError)

          // Final fallback: System default with comprehensive error message
          const isNetworkError = this.isNetworkRelatedError(mappingError) ||
            this.isNetworkRelatedError(destinationZoneError) ||
            this.isNetworkRelatedError(sourceZoneError)

          const errorMessage = isNetworkError
            ? 'Unable to connect to delivery service. Using standard rate.'
            : 'Delivery rate calculation temporarily unavailable. Using standard rate.'

          return {
            deliveryFee: 15.00,
            source: 'system_fallback',
            error: errorMessage
          }
        }
      }
    }
  },

  /**
   * Check if error is network-related
   */
  isNetworkRelatedError(error: unknown): boolean {
    if (!(error instanceof Error)) return false

    const message = error.message.toLowerCase()
    return message.includes('network') ||
      message.includes('timeout') ||
      message.includes('connection') ||
      message.includes('fetch') ||
      message.includes('unavailable')
  },

  /**
   * Circuit breaker state for API health monitoring
   */
  _circuitBreaker: {
    failures: 0,
    lastFailureTime: 0,
    state: 'closed' as 'closed' | 'open' | 'half-open'
  },

  /**
   * Check if circuit breaker allows requests
   */
  isCircuitBreakerOpen(): boolean {
    const { lastFailureTime, state } = this._circuitBreaker
    const now = Date.now()
    const cooldownPeriod = 30000 // 30 seconds

    if (state === 'open') {
      if (now - lastFailureTime > cooldownPeriod) {
        this._circuitBreaker.state = 'half-open'
        return false
      }
      return true
    }

    return false
  },

  /**
   * Record circuit breaker success
   */
  recordCircuitBreakerSuccess(): void {
    this._circuitBreaker.failures = 0
    this._circuitBreaker.state = 'closed'
  },

  /**
   * Record circuit breaker failure
   */
  recordCircuitBreakerFailure(): void {
    this._circuitBreaker.failures++
    this._circuitBreaker.lastFailureTime = Date.now()

    if (this._circuitBreaker.failures >= 3) {
      this._circuitBreaker.state = 'open'
      console.warn('Zone mapping API circuit breaker opened due to repeated failures')
    }
  },

  /**
   * Batch get delivery costs with enhanced error handling and circuit breaker
   */
  async getBatchDeliveryCosts(zonePairs: Array<{ fromZoneId: ObjectId; toZoneId: ObjectId }>): Promise<Map<string, { cost: number; source: string; error?: string }>> {
    const results = new Map<string, { cost: number; source: string; error?: string }>()

    // Check circuit breaker
    if (this.isCircuitBreakerOpen()) {
      console.warn('Zone mapping API circuit breaker is open, using fallback for all requests')

      // Use fallback for all pairs
      for (const { fromZoneId, toZoneId } of zonePairs) {
        const key = `${fromZoneId}->${toZoneId}`
        try {
          const zones = await deliveryService.getDeliveryZones()
          const destinationZone = zones.find(zone => zone.id === toZoneId)
          const cost = destinationZone?.delivery_cost || 15.00

          results.set(key, {
            cost,
            source: destinationZone ? 'zone_default' : 'system_fallback',
            error: 'Delivery service temporarily unavailable'
          })
        } catch (error) {
          results.set(key, {
            cost: 15.00,
            source: 'system_fallback',
            error: 'Unable to calculate delivery cost'
          })
        }
      }

      return results
    }

    // Process in parallel with limited concurrency and error isolation
    const batchSize = 3 // Reduced batch size for better error isolation
    let successCount = 0
    let failureCount = 0

    for (let i = 0; i < zonePairs.length; i += batchSize) {
      const batch = zonePairs.slice(i, i + batchSize)

      const batchPromises = batch.map(async ({ fromZoneId, toZoneId }) => {
        const key = `${fromZoneId}->${toZoneId}`
        try {
          const result = await this.calculateDeliveryFee(fromZoneId, toZoneId)
          results.set(key, {
            cost: result.deliveryFee,
            source: result.source,
            error: result.error || result.warning
          })
          successCount++
        } catch (error) {
          console.warn(`Batch delivery cost calculation failed for ${key}:`, error)
          results.set(key, {
            cost: 15.00,
            source: 'system_fallback',
            error: error instanceof Error ? error.message : 'Calculation failed'
          })
          failureCount++
        }
      })

      await Promise.all(batchPromises)

      // Add delay between batches to avoid overwhelming the API
      if (i + batchSize < zonePairs.length) {
        await new Promise(resolve => setTimeout(resolve, 100))
      }
    }

    // Update circuit breaker based on batch results
    const failureRate = failureCount / (successCount + failureCount)
    if (failureRate > 0.5) {
      this.recordCircuitBreakerFailure()
    } else if (successCount > 0) {
      this.recordCircuitBreakerSuccess()
    }

    return results
  },

  /**
   * Validate zone mapping availability
   */
  async validateZoneMapping(fromZoneId: ObjectId, toZoneId: ObjectId): Promise<{
    isAvailable: boolean;
    deliveryCost?: number;
    error?: string;
  }> {
    try {
      const deliveryCost = await this.getDeliveryCost(fromZoneId, toZoneId)
      return {
        isAvailable: true,
        deliveryCost
      }
    } catch (error) {
      return {
        isAvailable: false,
        error: error instanceof Error ? error.message : 'Zone mapping validation failed'
      }
    }
  }
}

// ============================================================================
// DATA TRANSFORMATION UTILITIES
// ============================================================================

/**
 * Format a 24-hour time string (HH:MM) to 12-hour display format (h:MM AM/PM)
 */
export function formatTime12h(time24: string): string {
  const [h, m] = time24.split(':').map(Number)
  const period = h >= 12 ? 'PM' : 'AM'
  const hour = h % 12 || 12
  return `${hour}:${m.toString().padStart(2, '0')} ${period}`
}

/**
 * Transform restaurant data for customer display (Trinidad timezone)
 */
function transformRestaurantToDisplayData(restaurant: Restaurant): RestaurantDisplayData {
  const isOpen = isRestaurantOpen(restaurant)

  // Calculate estimated delivery time from database fields or fallback to package score
  let estimatedDeliveryTime: string;

  if (restaurant.min_delivery_time && restaurant.max_delivery_time) {
    // Use database values if available
    estimatedDeliveryTime = `${restaurant.min_delivery_time}-${restaurant.max_delivery_time} minutes`;
  } else {
    // Fallback to package score mapping
    const deliveryTimeMap = {
      1: '20-30', // Package A
      2: '25-35', // Package B
      3: '30-40', // Package C
      0: '35-45', // Unassigned
    };
    estimatedDeliveryTime = deliveryTimeMap[restaurant.package_score] + ' minutes';
  }

  return {
    ...restaurant,
    isOpen,
    estimatedDeliveryTime,
    deliveryFee: 0, // Will be calculated based on selected delivery zone
    rating: 4.2, // This should come from reviews system
    reviewCount: 127, // This should come from reviews system
    ad_image: restaurant.ad_image || ' ', // Use restaurant image or placeholder
  }
}

/**
 * Transform menu item data for customer display
 */
function transformMenuItemToDisplayData(
  menuItem: MenuItem,
  customizations: MenuCustomisation[]
): MenuItemDisplayData {
  return {
    ...menuItem,
    customizations,
    isAvailable: true, // This should be based on restaurant hours and item availability
  }
}

// ============================================================================
// UTILITY FUNCTIONS
// ============================================================================

/**
 * Calculate total price including customizations
 */
export function calculateItemTotal(
  basePrice: number,
  quantity: number,
  customizations: Array<{ price: number }>
): number {
  const customizationTotal = customizations.reduce((sum, c) => sum + c.price, 0)
  return (basePrice + customizationTotal) * quantity
}

/**
 * Generate unique cart item ID
 */
export function generateCartItemId(): string {
  return `cart_${Date.now()}_${Math.random().toString(36).substring(2, 11)}`
}

/**
 * Check if restaurant is open at a specific time (Trinidad timezone)
 */
export function isRestaurantOpenAt(restaurant: Restaurant, date: Date = new Date()): boolean {
  // Use date directly with timeZone option — avoids double-conversion bugs
  const dayName = date.toLocaleDateString('en-US', {
    weekday: 'long',
    timeZone: 'America/Port_of_Spain'
  }).toLowerCase()
  const timeString = date.toLocaleTimeString('en-US', {
    hour12: false,
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Port_of_Spain'
  })

  const isClosedField = `${dayName}_is_closed` as keyof Restaurant
  const openField = `${dayName}_open` as keyof Restaurant
  const closeField = `${dayName}_close` as keyof Restaurant

  const isClosed = restaurant[isClosedField] as boolean
  const openTime = restaurant[openField] as string | undefined
  const closeTime = restaurant[closeField] as string | undefined

  if (isClosed || !openTime || !closeTime) {
    return false
  }

  return timeString >= openTime && timeString <= closeTime
}

/**
 * Generate order status history based on current status
 */
function generateOrderStatusHistory(order: Order): OrderStatusUpdate[] {
  const history: OrderStatusUpdate[] = []
  const createdAt = order.created_at
  const currentStatus = order.order_status

  // Always start with order creation
  history.push({
    status: 'pending',
    timestamp: createdAt,
    message: 'Order placed successfully',
  })

  // Add status updates based on current status
  const statusFlow: OrderStatus[] = ['pending', 'confirmed', 'placed', 'dispatched', 'delivered']
  const currentIndex = statusFlow.indexOf(currentStatus)

  if (currentIndex > 0) {
    // Generate timestamps for intermediate statuses
    const baseTime = new Date(createdAt).getTime()
    const intervals = [5, 10, 20, 25, 35] // Minutes after order creation

    for (let i = 1; i <= currentIndex; i++) {
      const statusTime = new Date(baseTime + intervals[i - 1] * 60 * 1000).toISOString()
      history.push({
        status: statusFlow[i],
        timestamp: statusTime,
        message: getStatusMessage(statusFlow[i]),
      })
    }
  }

  // Handle cancelled orders
  if (currentStatus === 'cancelled') {
    history.push({
      status: 'cancelled',
      timestamp: order.updated_at,
      message: 'Order was cancelled',
    })
  }

  return history
}

/**
 * Get user-friendly message for order status
 */
function getStatusMessage(status: OrderStatus): string {
  const messages: Record<OrderStatus, string> = {
    'pending': 'Order placed successfully',
    'confirmed': 'Restaurant confirmed your order',
    'placed': 'Your food is being prepared',
    'dispatched': 'Your order is on the way',
    'delivered': 'Order delivered successfully',
    'cancelled': 'Order was cancelled',
  }

  return messages[status] || 'Status updated'
}

/**
 * Calculate estimated delivery time based on order status and creation time (Trinidad timezone)
 */
function calculateEstimatedDeliveryTime(order: Order): string {
  const createdAt = new Date(order.created_at)
  // Use Trinidad timezone for current time
  const now = new Date(new Date().toLocaleString('en-US', { timeZone: 'America/Port_of_Spain' }))
  const elapsedMinutes = Math.floor((now.getTime() - createdAt.getTime()) / (1000 * 60))

  // Base delivery time estimates by status
  const timeEstimates: Record<OrderStatus, number> = {
    'pending': 45,
    'confirmed': 40,
    'placed': 30,
    'dispatched': 10,
    'delivered': 0,
    'cancelled': 0,
  }

  const baseEstimate = timeEstimates[order.order_status] || 30
  const remainingTime = Math.max(0, baseEstimate - elapsedMinutes)

  if (remainingTime === 0) {
    return order.order_status === 'delivered' ? 'Delivered' : 'Any moment now'
  }

  return `${remainingTime}-${remainingTime + 10} minutes`
}

/**
 * Validate order items before submission
 */
export function validateOrderItems(items: CartItem[]): {
  isValid: boolean;
  errors: string[];
} {
  const errors: string[] = []

  if (!items.length) {
    errors.push('Cart is empty')
  }

  items.forEach((item, index) => {
    if (!item.menuItemId) {
      errors.push(`Item ${index + 1}: Missing menu item ID`)
    }
    if (item.quantity <= 0) {
      errors.push(`Item ${index + 1}: Invalid quantity`)
    }
    if (item.totalPrice <= 0) {
      errors.push(`Item ${index + 1}: Invalid price`)
    }
  })

  return {
    isValid: errors.length === 0,
    errors,
  }
}

/**
 * Format order for display
 */
export function formatOrderForDisplay(order: Order): {
  orderNumber: string;
  formattedDate: string;
  formattedTime: string;
  statusDisplay: string;
  totalDisplay: string;
} {
  const date = new Date(order.created_at)
  // Convert to Trinidad timezone for display
  const trinidadDate = new Date(date.toLocaleString('en-US', { timeZone: 'America/Port_of_Spain' }))

  return {
    orderNumber: `#${order.id.slice(-8).toUpperCase()}`,
    formattedDate: trinidadDate.toLocaleDateString('en-US', { timeZone: 'America/Port_of_Spain' }),
    formattedTime: trinidadDate.toLocaleTimeString('en-US', {
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Port_of_Spain'
    }),
    statusDisplay: order.order_status,
    totalDisplay: order.total_amount ? `$${order.total_amount.toFixed(2)}` : 'N/A',
  }
}
